import type { Content } from '../../types/UcContent';
import { schemaForTemplate } from './screen-schemas/index';
import type {
  EntryPrefetchKind,
  IteratedScreenSlot,
  ScreenSchema,
  ScreenSlot
} from './screen-schemas/types';
import { isIteratedSlot } from './screen-schemas/types';
import {
  buildHsgIntroSectionText,
  buildHsgLevelText
} from './hotspotGameAudio';
import {
  createScreenAudioTextResolver,
  hotspotTargetLightboxId,
  narrationTextId,
  readPlaceholderValues,
  synthHotspotTargetLightboxShell,
  type NarratableScreen,
  type ResolveTextFn
} from './screenAudioText';
import {
  splitTemplateResolvedIntroTargets,
  synthTemplatesChildId,
  TEMPLATE_SKIP_INTRO_CHAIN,
  withInheritedPlaceholderValues
} from './useScreenAudio';
import screenSplitSchema from './screen-schemas/screen-split.schema';

export interface JitPrefetchTarget {
  key: string;
  text: string;
}

export interface JitEntryPrefetchContext {
  compiled: Content;
  locale: string;
  resolveText: ResolveTextFn;
  /** Page title for unit-title / subunit-title (runtime `pageTitle` inject). */
  pageTitle?: string;
  /** Edit path for split template resolution (e.g. `pages.0.screens.2`). */
  splitEditPath?: string;
}

const hasTier1Intro = (screen: NarratableScreen): boolean =>
  Boolean(screen.audio || screen.speech);

const pushTarget = (
  targets: JitPrefetchTarget[],
  key: string,
  text: string
) => {
  const trimmed = text?.trim();
  if (!key || !trimmed) return;
  targets.push({ key, text: trimmed });
};

const slotIncludedForAudio = (
  slot: ScreenSlot | IteratedScreenSlot
): boolean => {
  const use = slot.use ?? 'all';
  return use === 'audio' || use === 'all';
};

const itemHasTier1Audio = (
  item: Record<string, unknown> | undefined
): boolean => Boolean(item?.audio || item?.speech);

const isOverlayLightboxTemplate = (template: string): boolean =>
  template === 'lightbox' || template === 'lightbox-video';

/**
 * Intro JiT target for a template child, using the correct S3/JiT cache key
 * (`lightbox-${id}` for lightbox shells, etc.).
 */
const introPrefetchTargetForChild = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): { key: string; text: string } | null => {
  if (!screen?.id) return null;
  const schema =
    schemaForTemplate(screen.template as string | undefined) ??
    (isOverlayLightboxTemplate(String(screen.template ?? ''))
      ? schemaForTemplate('lightbox')
      : null);
  if (!schema) return null;

  const introSlot = schema.slots['intro'];
  if (!introSlot || isIteratedSlot(introSlot)) return null;

  const fill = readPlaceholderValues(screen as Record<string, unknown>);
  const api = createScreenAudioTextResolver({
    screen,
    schema,
    resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
  });

  const text = screen['text-audio']
    ? (ctx.resolveText(
        `${narrationTextId(screen) ?? screen.id}.text-audio`,
        screen['text-audio'] as string | number,
        fill
      ) ?? '')
    : api.getIntroJitText();

  if (!text.trim()) return null;
  return { key: api.resolveAudioKey('intro'), text };
};

/** Nested `templates[]` inside a lightbox (mirrors `uc-lightbox` open follow-on queue). */
const collectLightboxNestedTemplateTargets = (
  lightbox: NarratableScreen,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  const nested = lightbox.templates as NarratableScreen[] | undefined;
  if (!Array.isArray(nested) || !lightbox.id) return;

  const parentRecord = lightbox as unknown as Record<string, unknown>;
  nested.forEach((raw, i) => {
    const childWithId = (
      raw.id ? raw : { ...raw, id: synthTemplatesChildId(lightbox.id!, i) }
    ) as NarratableScreen;
    const child = withInheritedPlaceholderValues(
      childWithId as Record<string, unknown>,
      parentRecord
    ) as NarratableScreen;
    const t = String(child.template ?? '');
    if (isOverlayLightboxTemplate(t) || t === 'hotspot-fullscreen') return;
    if (child.audio || child.speech) return;

    const intro = introPrefetchTargetForChild(child, ctx);
    if (intro) pushTarget(targets, intro.key, intro.text);
  });
};

/** Warm overlay lightbox shell + nested template intros (playback still skips on section entry). */
const prefetchOverlayLightbox = (
  child: NarratableScreen,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  if (child.audio || child.speech) return;
  const shell = introPrefetchTargetForChild(child, ctx);
  if (shell) pushTarget(targets, shell.key, shell.text);
  collectLightboxNestedTemplateTargets(child, ctx, targets);
};

/**
 * Warm JiT for hotspot `targets.*` lightboxes (mounted via `uc-lightbox`, not `templates[]`).
 * Also walks `hotspot-game` `levels[].targets`.
 */
const collectHotspotTargetsForHost = (
  hostId: string,
  targets: Record<string, unknown[]> | undefined,
  parentRecord: Record<string, unknown>,
  ctx: JitEntryPrefetchContext,
  out: JitPrefetchTarget[]
) => {
  if (!hostId || !targets) return;

  for (const [name, rows] of Object.entries(targets)) {
    if (!Array.isArray(rows) || rows.length === 0) continue;

    if (rows.length > 1) {
      rows.forEach((raw, i) => {
        const row = raw as Record<string, unknown>;
        if (row.audio || row.speech) return;
        const childId =
          (row.id as string) || `${hotspotTargetLightboxId(hostId, name)}-${i}`;
        const child = withInheritedPlaceholderValues(
          { ...row, id: childId } as NarratableScreen,
          parentRecord
        ) as NarratableScreen;
        const t = String(child.template ?? '');
        if (isOverlayLightboxTemplate(t)) {
          prefetchOverlayLightbox(child, ctx, out);
          return;
        }
        const intro = introPrefetchTargetForChild(child, ctx);
        if (intro) pushTarget(out, intro.key, intro.text);
      });
      continue;
    }

    const first = rows[0] as Record<string, unknown>;
    if (first.audio || first.speech) continue;

    const t = String(first.template ?? '');
    if (isOverlayLightboxTemplate(t)) {
      const child = withInheritedPlaceholderValues(
        (first.id
          ? first
          : {
              ...first,
              id: hotspotTargetLightboxId(hostId, name)
            }) as NarratableScreen,
        parentRecord
      ) as NarratableScreen;
      prefetchOverlayLightbox(child, ctx, out);
      continue;
    }

    prefetchOverlayLightbox(
      synthHotspotTargetLightboxShell(hostId, name, first),
      ctx,
      out
    );
  }
};

const collectHotspotTargetLightboxes = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  const template = String(screen.template ?? '');
  const parentRecord = screen as unknown as Record<string, unknown>;

  if (template === 'hotspot' || template === 'hotspot-fullscreen') {
    if (!screen.id) return;
    collectHotspotTargetsForHost(
      screen.id,
      screen.targets as Record<string, unknown[]> | undefined,
      parentRecord,
      ctx,
      targets
    );
    return;
  }

  if (template === 'hotspot-game') {
    const levels = screen.levels as Array<Record<string, unknown>> | undefined;
    if (!Array.isArray(levels)) return;
    levels.forEach(level => {
      const levelId = level.id as string | undefined;
      if (!levelId) return;
      collectHotspotTargetsForHost(
        levelId,
        level.targets as Record<string, unknown[]> | undefined,
        { ...parentRecord, ...level },
        ctx,
        targets
      );
    });
  }
};

/** Dedupe by cache key + text (course-title title/subtitle share one marker key). */
const dedupeTargets = (targets: JitPrefetchTarget[]): JitPrefetchTarget[] => {
  const seen = new Set<string>();
  const out: JitPrefetchTarget[] = [];
  for (const t of targets) {
    const id = `${t.key}\0${t.text}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(t);
  }
  return out;
};

/**
 * Walk every audio slot on a screen from its schema (intro, feedback, all tab indices, etc.).
 */
const collectSchemaSlotsOnScreen = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  const fill = readPlaceholderValues(screen as Record<string, unknown>);
  const api = createScreenAudioTextResolver({
    screen,
    schema,
    resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
  });

  for (const [slotName, slotDef] of Object.entries(schema.slots)) {
    if (!slotIncludedForAudio(slotDef)) continue;

    if (isIteratedSlot(slotDef)) {
      const items = screen[slotDef.iterate as keyof NarratableScreen] as
        | unknown[]
        | undefined;
      if (!Array.isArray(items)) continue;

      items.forEach((rawItem, index) => {
        const item = rawItem as Record<string, unknown>;
        if (itemHasTier1Audio(item)) return;
        const slotKey = `${slotDef.keyPrefix}-${index}`;
        pushTarget(
          targets,
          api.resolveAudioKey(slotKey),
          api.getSlotText(slotKey)
        );
      });
      continue;
    }

    if (slotName === 'intro') {
      if (hasTier1Intro(screen)) continue;
      pushTarget(targets, api.resolveAudioKey('intro'), api.getIntroJitText());
      continue;
    }

    if (
      String(screen.template) === 'hotspot-game' &&
      slotName === 'intro-section'
    ) {
      pushTarget(
        targets,
        api.resolveAudioKey('intro-section'),
        buildHsgIntroSectionText(screen, (k, v, values) =>
          ctx.resolveText(k, v, values ?? fill)
        )
      );
      continue;
    }

    pushTarget(
      targets,
      api.resolveAudioKey(slotName),
      api.getSlotText(slotName)
    );
  }
};

const collectTemplatesHostAllSlots = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  const hostId = screen.id ?? 'host';
  const host = (
    screen.id ? screen : { ...screen, id: hostId }
  ) as NarratableScreen;

  collectSchemaSlotsOnScreen(host, schema, ctx, targets);

  const templates = host.templates as NarratableScreen[] | undefined;
  if (!Array.isArray(templates)) return;

  const parentRecord = host as unknown as Record<string, unknown>;

  templates.forEach((raw, i) => {
    const childWithId = (
      raw.id ? raw : { ...raw, id: synthTemplatesChildId(hostId, i) }
    ) as NarratableScreen;
    const child = withInheritedPlaceholderValues(
      childWithId as Record<string, unknown>,
      parentRecord
    ) as NarratableScreen;
    const t = String(child.template ?? '');

    if (isOverlayLightboxTemplate(t)) {
      prefetchOverlayLightbox(child, ctx, targets);
      return;
    }

    if (TEMPLATE_SKIP_INTRO_CHAIN.has(t)) return;

    if (t === 'split' || t === 'split-2-1' || t === 'split-1-2') {
      const splitPath = `${ctx.splitEditPath ?? 'templates'}.templates.${i}`;
      const splitTargets = resolveJitAllSlotTargets(child, {
        ...ctx,
        splitEditPath: splitPath
      });
      targets.push(...splitTargets);
      return;
    }

    if (child.audio || child.speech) return;

    const intro = childIntroText(child, ctx);
    if (intro) pushTarget(targets, intro.key, intro.text);

    const childSchema = schemaForTemplate(child.template as string | undefined);
    if (childSchema)
      collectSchemaSlotsOnScreen(child, childSchema, ctx, targets);
  });
};

/**
 * All JiT slots for a single carousel / slides child slide row.
 *
 * Split (`split`, `split-2-1`, `split-1-2`) and `templates[]`-chain slides carry
 * their narration in nested left/right panels or chained children, not on the
 * slide's own intro slot — so delegate to {@link resolveJitAllSlotTargets},
 * which already dives those structures. Plain template slides keep the simple
 * intro + schema-slot collection.
 */
const collectSlideChildAllSlots = (
  slide: NarratableScreen,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  if (slide.audio || slide.speech) return;

  const t = String(slide.template ?? '');
  const isSplit = t === 'split' || t === 'split-2-1' || t === 'split-1-2';
  const hasTemplatesChain =
    Array.isArray(slide.templates) && slide.templates.length > 0;

  if (isSplit || hasTemplatesChain) {
    targets.push(...resolveJitAllSlotTargets(slide, ctx));
    return;
  }

  const intro = childIntroText(slide, ctx);
  if (intro) pushTarget(targets, intro.key, intro.text);

  const childSchema = schemaForTemplate(slide.template as string | undefined);
  if (childSchema) {
    collectSchemaSlotsOnScreen(slide, childSchema, ctx, targets);
  }
};

const collectSlidesAllSlots = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  const slides = screen.slides as NarratableScreen[] | undefined;
  if (!Array.isArray(slides)) return;

  const parentRecord = screen as unknown as Record<string, unknown>;

  slides.forEach((raw, i) => {
    const slideWithId = withInheritedPlaceholderValues(
      { ...raw, id: raw.id ?? `${screen.id}-slide-${i}` } as Record<
        string,
        unknown
      >,
      parentRecord
    ) as NarratableScreen;

    collectSlideChildAllSlots(slideWithId, ctx, targets);
  });
};

/**
 * Carousel all-slot collection: carousel intro + each slide. Plain (no-template)
 * slides are read through the carousel's own `slide` iterated slot; template /
 * split / `templates[]` slides are dived via {@link collectSlideChildAllSlots}
 * so their nested narration is warmed (matches `screen-carousel.vue` playback).
 */
const collectCarouselAllSlots = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  collectSchemaSlotsOnScreen(screen, schema, ctx, targets);

  const slides = screen.slides as NarratableScreen[] | undefined;
  if (!Array.isArray(slides)) return;

  const parentRecord = screen as unknown as Record<string, unknown>;

  slides.forEach((raw, i) => {
    const hasTemplatesChain =
      Array.isArray(raw.templates) && raw.templates.length > 0;
    // Plain content slides are already covered by the carousel `slide` slot above.
    if (!raw.template && !hasTemplatesChain) return;

    const slideWithId = withInheritedPlaceholderValues(
      { ...raw, id: raw.id ?? `${screen.id}-slide-${i}` } as Record<
        string,
        unknown
      >,
      parentRecord
    ) as NarratableScreen;

    collectSlideChildAllSlots(slideWithId, ctx, targets);
  });
};

const childIntroText = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): { key: string; text: string } | null =>
  introPrefetchTargetForChild(screen, ctx);

const defaultIntro = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  if (hasTier1Intro(screen)) return [];
  const fill = readPlaceholderValues(screen as Record<string, unknown>);
  const api = createScreenAudioTextResolver({
    screen,
    schema,
    resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
  });
  const text = api.getIntroJitText();
  return text.trim() ? [{ key: api.resolveAudioKey('intro'), text }] : [];
};

const resolveTabsEntry = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  const targets = defaultIntro(screen, schema, ctx);
  const tabs = screen.tabs as Array<Record<string, unknown>> | undefined;
  const i = 0;
  const tab = tabs?.[i];
  if (!tab) return targets;
  if (tab.audio || tab.speech) return targets;

  const fill = readPlaceholderValues(screen as Record<string, unknown>);
  const api = createScreenAudioTextResolver({
    screen,
    schema,
    resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
  });
  pushTarget(
    targets,
    api.resolveAudioKey(`tab-${i}`),
    api.getSlotText(`tab-${i}`)
  );
  return targets;
};

const resolveSplitChain = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  const editPath = ctx.splitEditPath ?? `split-${screen.id ?? 'host'}`;
  const { host, leftForIntro, rightForIntro } =
    splitTemplateResolvedIntroTargets(screen, editPath);
  const targets: JitPrefetchTarget[] = [];
  const hostSchema = screenSplitSchema;
  const fill = readPlaceholderValues(host as Record<string, unknown>);

  if (!hasTier1Intro(host)) {
    const api = createScreenAudioTextResolver({
      screen: host,
      schema: hostSchema,
      resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
    });
    pushTarget(targets, api.resolveAudioKey('intro'), api.getIntroJitText());
  }

  for (const child of [leftForIntro, rightForIntro]) {
    if (!child) continue;
    if (child.audio || child.speech) continue;

    // Panel child wraps its own templates[] chain (deep nesting) — warm the chain.
    const childTemplates = child.templates as NarratableScreen[] | undefined;
    if (Array.isArray(childTemplates) && childTemplates.length > 0) {
      const childSchema = schemaForTemplate(
        child.template as string | undefined
      );
      if (childSchema) {
        targets.push(...resolveTemplatesChain(child, childSchema, ctx));
        continue;
      }
    }

    const intro = childIntroText(child, ctx);
    if (intro) pushTarget(targets, intro.key, intro.text);
  }

  return targets;
};

const resolveTemplatesChain = (
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  const hostId = screen.id ?? 'host';
  const host = (
    screen.id ? screen : { ...screen, id: hostId }
  ) as NarratableScreen;
  const targets = defaultIntro(host, schema, ctx);
  const templates = host.templates as NarratableScreen[] | undefined;
  if (!Array.isArray(templates)) return targets;

  const parentRecord = host as unknown as Record<string, unknown>;

  templates.forEach((raw, i) => {
    const childWithId = (
      raw.id ? raw : { ...raw, id: synthTemplatesChildId(hostId, i) }
    ) as NarratableScreen;
    const child = withInheritedPlaceholderValues(
      childWithId as Record<string, unknown>,
      parentRecord
    ) as NarratableScreen;
    const t = String(child.template ?? '');

    if (isOverlayLightboxTemplate(t)) {
      prefetchOverlayLightbox(child, ctx, targets);
      return;
    }

    if (TEMPLATE_SKIP_INTRO_CHAIN.has(t)) return;

    if (t === 'split' || t === 'split-2-1' || t === 'split-1-2') {
      const splitPath = `${ctx.splitEditPath ?? 'templates'}.templates.${i}`;
      targets.push(
        ...resolveSplitChain(child, { ...ctx, splitEditPath: splitPath })
      );
      return;
    }

    if (child.audio || child.speech) return;
    const intro = childIntroText(child, ctx);
    if (intro) pushTarget(targets, intro.key, intro.text);
  });

  return targets;
};

// ---------------------------------------------------------------------------
// Sticky (sticky-left / sticky-right). The schema has no slots — content lives
// in left[]/right[] (or *-edge[]) child screens, audio-keyed at runtime by
// screen-sticky.vue as `${id}_${side}_${i}` (pinned side index 0 only; scrolling
// side every index). Mirror that here so warm keys match playback.
// ---------------------------------------------------------------------------

const stickyChildScreens = (
  screen: NarratableScreen
): { pinned: NarratableScreen | null; scrolling: NarratableScreen[] } => {
  const isLeft = String(screen.template ?? '') === 'sticky-left';
  const pinnedSide = isLeft ? 'left' : 'right';
  const scrollingSide = isLeft ? 'right' : 'left';
  const rec = screen as unknown as Record<string, unknown>;

  const sideArray = (side: string): NarratableScreen[] =>
    ((rec[side] ?? rec[`${side}-edge`]) as NarratableScreen[] | undefined) ??
    [];

  const withId = (
    raw: NarratableScreen,
    side: string,
    i: number
  ): NarratableScreen =>
    withInheritedPlaceholderValues(
      { ...raw, id: raw.id ?? `${screen.id}_${side}_${i}` } as Record<
        string,
        unknown
      >,
      rec
    ) as NarratableScreen;

  const pinnedRaw = sideArray(pinnedSide)[0];
  return {
    pinned: pinnedRaw ? withId(pinnedRaw, pinnedSide, 0) : null,
    scrolling: sideArray(scrollingSide).map((raw, i) =>
      withId(raw, scrollingSide, i)
    )
  };
};

/** On-enter targets for one sticky child (mirrors resolveSplitChain's per-child loop). */
const collectStickyChildEntry = (
  child: NarratableScreen | null,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  if (!child) return;
  if (child.audio || child.speech) return;

  const t = String(child.template ?? '');
  if (t === 'split' || t === 'split-2-1' || t === 'split-1-2') {
    targets.push(...resolveSplitChain(child, ctx));
    return;
  }

  const childTemplates = child.templates as NarratableScreen[] | undefined;
  if (Array.isArray(childTemplates) && childTemplates.length > 0) {
    const childSchema = schemaForTemplate(child.template as string | undefined);
    if (childSchema) {
      targets.push(...resolveTemplatesChain(child, childSchema, ctx));
      return;
    }
  }

  const intro = childIntroText(child, ctx);
  if (intro) pushTarget(targets, intro.key, intro.text);
};

/** All-slot targets for one sticky child (mirrors the split branch of resolveJitAllSlotTargets). */
const collectStickyChildAllSlots = (
  child: NarratableScreen | null,
  ctx: JitEntryPrefetchContext,
  targets: JitPrefetchTarget[]
) => {
  if (!child) return;
  if (child.audio || child.speech) return;

  const childSchema = schemaForTemplate(child.template as string | undefined);
  if (childSchema) {
    // resolveJitAllSlotTargets recurses into nested split / templates / carousel.
    targets.push(...resolveJitAllSlotTargets(child, ctx));
  } else {
    const intro = childIntroText(child, ctx);
    if (intro) pushTarget(targets, intro.key, intro.text);
  }
};

/** On enter, sticky plays the pinned child-0 then queues the first scrolling child. */
const resolveStickyChain = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  const { pinned, scrolling } = stickyChildScreens(screen);
  const targets: JitPrefetchTarget[] = [];
  collectStickyChildEntry(pinned, ctx, targets);
  collectStickyChildEntry(scrolling[0] ?? null, ctx, targets);
  return targets;
};

const resolveCourseTitle = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  if (hasTier1Intro(screen)) return [];
  const course = ctx.compiled.course;
  const targets: JitPrefetchTarget[] = [];
  if (!screen.id) return targets;

  if (course?.title) {
    pushTarget(
      targets,
      screen.id,
      ctx.resolveText('course.title', course.title) ?? String(course.title)
    );
  }
  if (course?.subtitle) {
    pushTarget(
      targets,
      screen.id,
      ctx.resolveText('course.subtitle', course.subtitle) ??
        String(course.subtitle)
    );
  }
  return targets;
};

const resolveUnitTitle = (
  screen: NarratableScreen,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  if (hasTier1Intro(screen)) return [];
  const pageTitle =
    ctx.pageTitle ??
    findPageTitleForScreen(ctx.compiled, screen.id) ??
    (screen.title as string | undefined);
  if (!screen.id || !pageTitle) return [];
  const text =
    ctx.resolveText(`${screen.id}.title`, pageTitle) ?? String(pageTitle);
  return text.trim() ? [{ key: screen.id, text }] : [];
};

function findPageTitleForScreen(
  compiled: Content,
  screenId?: string
): string | undefined {
  if (!screenId) return undefined;
  for (const page of compiled.pages ?? []) {
    const found = page.screens?.some(s => s.id === screenId);
    if (found) return page.title as string | undefined;
  }
  return undefined;
}

const ENTRY_BY_KIND: Record<
  string,
  (
    screen: NarratableScreen,
    schema: ScreenSchema,
    ctx: JitEntryPrefetchContext
  ) => JitPrefetchTarget[]
> = {
  intro: defaultIntro,
  'intro-and-initial-tab': resolveTabsEntry,
  'split-chain': (s, _sch, ctx) => resolveSplitChain(s, ctx),
  'sticky-chain': (s, _sch, ctx) => resolveStickyChain(s, ctx),
  'templates-chain': resolveTemplatesChain,
  'course-title': (s, _sch, ctx) => resolveCourseTitle(s, ctx),
  'unit-title': (s, _sch, ctx) => resolveUnitTitle(s, ctx),
  'timeline-intro': defaultIntro,
  'carousel-intro-and-slide-0': (screen, schema, ctx) => {
    const targets = defaultIntro(screen, schema, ctx);
    const slide0 = (
      screen.slides as Record<string, unknown>[] | undefined
    )?.[0];
    if (!slide0) return targets;
    if (slide0.audio || slide0.speech) return targets;

    const t = String(slide0.template ?? '');
    const isSplit = t === 'split' || t === 'split-2-1' || t === 'split-1-2';
    const hasTemplatesChain =
      Array.isArray(slide0.templates) && slide0.templates.length > 0;

    // Split / templates-chain slides carry their on-enter narration in nested
    // panels or chained children (matches `screen-carousel.vue` slide-0 queue).
    if (isSplit || hasTemplatesChain || slide0.template) {
      const slideWithId = withInheritedPlaceholderValues(
        { ...slide0, id: slide0.id ?? `${screen.id}-slide-0` } as Record<
          string,
          unknown
        >,
        screen as unknown as Record<string, unknown>
      ) as NarratableScreen;

      if (isSplit) {
        targets.push(...resolveSplitChain(slideWithId, ctx));
      } else if (hasTemplatesChain) {
        const childSchema =
          schemaForTemplate(slideWithId.template as string | undefined) ??
          schema;
        targets.push(...resolveTemplatesChain(slideWithId, childSchema, ctx));
      } else {
        const intro = childIntroText(slideWithId, ctx);
        if (intro) pushTarget(targets, intro.key, intro.text);
      }
      return targets;
    }

    const fill = readPlaceholderValues(screen as Record<string, unknown>);
    const api = createScreenAudioTextResolver({
      screen,
      schema,
      resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
    });
    pushTarget(
      targets,
      api.resolveAudioKey('slide-0'),
      api.getSlotText('slide-0')
    );
    return targets;
  },
  'blinds-intro-and-initial': (screen, schema, ctx) => {
    const targets = defaultIntro(screen, schema, ctx);
    const fill = readPlaceholderValues(screen as Record<string, unknown>);
    const api = createScreenAudioTextResolver({
      screen,
      schema,
      resolveText: (k, v, values) => ctx.resolveText(k, v, values ?? fill)
    });
    const card0 = (screen.cards as Record<string, unknown>[] | undefined)?.[0];
    if (card0?.audio || card0?.speech) return targets;
    pushTarget(
      targets,
      api.resolveAudioKey('blind-0'),
      api.getSlotText('blind-0')
    );
    return targets;
  },
  'flipcards-intro': defaultIntro,
  'accordion-intro': defaultIntro,
  'slides-first-child-intro': (screen, _schema, ctx) => {
    const slides = screen.slides as NarratableScreen[] | undefined;
    const first = slides?.[0];
    if (!first) return [];
    const slideWithId = {
      ...first,
      id: first.id ?? `${screen.id}-slide-0`
    } as NarratableScreen;
    if (slideWithId.audio || slideWithId.speech) return [];
    const intro = childIntroText(slideWithId, ctx);
    return intro ? [{ key: intro.key, text: intro.text }] : [];
  },
  'hotspot-game-entry': (screen, _schema, ctx) => {
    if (!screen.id) return [];
    const fill = readPlaceholderValues(screen as Record<string, unknown>);
    const targets: JitPrefetchTarget[] = [];
    const introText = buildHsgIntroSectionText(screen, (k, v, values) =>
      ctx.resolveText(k, v, values ?? fill)
    );
    pushTarget(targets, `${screen.id}_intro`, introText);
    const level0 = buildHsgLevelText(screen, 0, (k, v, values) =>
      ctx.resolveText(k, v, values ?? fill)
    );
    if (level0.trim()) pushTarget(targets, `${screen.id}-level-0`, level0);
    return targets;
  }
};

/** Fallback when `schema.entryPrefetch` is unset but template manages its own audio. */
const TEMPLATE_ENTRY_KIND: Record<string, EntryPrefetchKind> = {
  tabs: 'intro-and-initial-tab',
  split: 'split-chain',
  'split-2-1': 'split-chain',
  'split-1-2': 'split-chain',
  'sticky-left': 'sticky-chain',
  'sticky-right': 'sticky-chain',
  'course-title': 'course-title',
  'unit-title': 'unit-title',
  'subunit-title': 'unit-title',
  'esg-title': 'course-title',
  timeline: 'timeline-intro',
  carousel: 'carousel-intro-and-slide-0',
  blinds: 'blinds-intro-and-initial',
  'flip-cards': 'flipcards-intro',
  accordion: 'accordion-intro',
  slides: 'slides-first-child-intro'
};

const resolveByKind = (
  kind: EntryPrefetchKind,
  screen: NarratableScreen,
  schema: ScreenSchema,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] => {
  const fn = ENTRY_BY_KIND[kind] ?? defaultIntro;
  return fn(screen, schema, ctx);
};

/**
 * Every JiT warm target declared on the screen's schema (all tabs, feedback
 * variants, flip-cards, carousel slides, etc.). Used for full-screen prefetch
 * on section enter and {@link extractJitAudioScript}.
 *
 * Quiz / bank inner sections are only included when their `id` appears as a
 * top-level `pages[].screens[]` row resolvable via `findScreenById`.
 */
export function resolveJitAllSlotTargets(
  screen: NarratableScreen | null | undefined,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] {
  if (!screen?.id || screen['dont-read']) return [];

  const schema = schemaForTemplate(screen.template as string | undefined);
  if (!schema) return [];

  const targets: JitPrefetchTarget[] = [];
  const template = String(screen.template ?? '');

  if (template === 'course-title' || template === 'esg-title') {
    targets.push(...resolveCourseTitle(screen, ctx));
    return dedupeTargets(targets);
  }

  if (template === 'unit-title' || template === 'subunit-title') {
    targets.push(...resolveUnitTitle(screen, ctx));
    return dedupeTargets(targets);
  }

  if (template === 'slides') {
    collectSlidesAllSlots(screen, ctx, targets);
    return dedupeTargets(targets);
  }

  if (template === 'carousel') {
    collectCarouselAllSlots(screen, schema, ctx, targets);
    return dedupeTargets(targets);
  }

  const templates = screen.templates as NarratableScreen[] | undefined;
  if (Array.isArray(templates) && templates.length > 0) {
    collectTemplatesHostAllSlots(screen, schema, ctx, targets);
    return dedupeTargets(targets);
  }

  if (
    template === 'split' ||
    template === 'split-2-1' ||
    template === 'split-1-2'
  ) {
    const { host, leftForIntro, rightForIntro } =
      splitTemplateResolvedIntroTargets(
        screen,
        ctx.splitEditPath ?? `split-${screen.id ?? 'host'}`
      );
    collectSchemaSlotsOnScreen(host, screenSplitSchema, ctx, targets);
    for (const child of [leftForIntro, rightForIntro]) {
      if (!child) continue;
      const childSchema = schemaForTemplate(
        child.template as string | undefined
      );
      const childTemplates = child.templates as NarratableScreen[] | undefined;
      if (
        childSchema &&
        Array.isArray(childTemplates) &&
        childTemplates.length > 0
      ) {
        // Panel child wraps its own templates[] chain (deep nesting).
        collectTemplatesHostAllSlots(child, childSchema, ctx, targets);
      } else if (childSchema) {
        collectSchemaSlotsOnScreen(child, childSchema, ctx, targets);
      } else {
        const intro = childIntroText(child, ctx);
        if (intro && !child.audio && !child.speech) {
          pushTarget(targets, intro.key, intro.text);
        }
      }
    }
    return dedupeTargets(targets);
  }

  if (template === 'sticky-left' || template === 'sticky-right') {
    const { pinned, scrolling } = stickyChildScreens(screen);
    for (const child of [pinned, ...scrolling]) {
      collectStickyChildAllSlots(child, ctx, targets);
    }
    return dedupeTargets(targets);
  }

  collectSchemaSlotsOnScreen(screen, schema, ctx, targets);

  if (
    template === 'hotspot' ||
    template === 'hotspot-fullscreen' ||
    template === 'hotspot-game'
  ) {
    collectHotspotTargetLightboxes(screen, ctx, targets);
  }

  return dedupeTargets(targets);
}

/**
 * All slot targets except those in the on-enter entry sequence (for tests/docs).
 */
export function resolveJitRemainingScreenTargets(
  screen: NarratableScreen | null | undefined,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] {
  const all = resolveJitAllSlotTargets(screen, ctx);
  const entryKeys = new Set(
    resolveJitEntryTargets(screen, ctx).map(t => `${t.key}\0${t.text}`)
  );
  return all.filter(t => !entryKeys.has(`${t.key}\0${t.text}`));
}

/**
 * Resolves JiT cache warm targets for a screen's on-enter audio sequence.
 * Returns empty when the screen should not use JiT prefetch.
 */
export function resolveJitEntryTargets(
  screen: NarratableScreen | null | undefined,
  ctx: JitEntryPrefetchContext
): JitPrefetchTarget[] {
  if (!screen?.id || screen['dont-read']) return [];

  const schema = schemaForTemplate(screen.template as string | undefined);
  if (!schema) return [];

  if (hasTier1Intro(screen)) return [];

  const templates = screen.templates as NarratableScreen[] | undefined;
  if (Array.isArray(templates) && templates.length > 0) {
    return resolveByKind('templates-chain', screen, schema, ctx);
  }

  const template = String(screen.template ?? '');
  const kind =
    schema.entryPrefetch ??
    (schema.managesOwnSectionAudio ? TEMPLATE_ENTRY_KIND[template] : 'intro');

  if (!kind) return defaultIntro(screen, schema, ctx);

  if (
    kind === 'split-chain' ||
    template === 'split' ||
    template === 'split-2-1' ||
    template === 'split-1-2'
  ) {
    return resolveSplitChain(screen, ctx);
  }

  return resolveByKind(kind, screen, schema, ctx);
}
